import React, { useCallback, useEffect, useMemo, useState } from 'react';
import map from 'lodash/map';
import omit from 'lodash/omit';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { sha256hex } from '@zedoc/sha256';
import { ADMIN_CREATE_PROJECT } from '../../common/permissions';
import { default as ProjectSelect } from '../../common/selectors/Project';
import Button from '../../components/Button';
import Stack from '../../common/components/primitives/Stack';
import List from '../../common/components/List';
import Search from '../../components/inputs/Search';
import { apiAdminSearchProjects } from '../../common/api/admin';
import settings from '../../common/settings';
import { dialog as dialogCreateProject } from './components/DialogCreateProject';
import DialogArchiveProject, {
  dialog as dialogArchiveProject,
} from './components/DialogArchiveProject';
import ProjectItem from './components/ProjectItem';
import useDocumentTitle from '../../utils/useDocumentTitle';
import usePagination from '../../utils/usePagination';
import usePermission from '../../utils/usePermission';
import useHighlight from '../../utils/useHighlight';
import { getFilters, getSorter } from './store';
import Card from '../../components/Card';
import CurrentUser from '../../models/CurrentUser';

const {
  features: { enableProjectWizardV2 },
} = settings.public;

const getErrorText = (project) => {
  if (project.blueprintInconsistent) {
    return '[MISCONFIGURED]';
  }
  return null;
};

const SettingsProjects = React.memo(() => {
  const history = useHistory();
  const location = useLocation();
  const [currentPageState, setCurrentPageState] = useState();
  const [publishRefresh, setPublishRefresh] = useState(
    () => location.state?.publishRefresh,
  );
  const filters = useSelector(getFilters);
  const sorter = useSelector(getSorter);
  const canCreateProjects = usePermission([ADMIN_CREATE_PROJECT]);
  const { t } = useTranslation();
  useDocumentTitle([t('settings'), t('projects')]);
  const currentUser = useSelector(CurrentUser.select.user());
  const { pinnedItems = {} } = currentUser;

  useEffect(() => {
    if (location.state?.publishRefresh) {
      setPublishRefresh(location.state.publishRefresh);
      history.replace('/settings/projects');
    }
  }, [history, location.state]);

  const version = useMemo(
    () =>
      sha256hex(
        JSON.stringify({
          ...pinnedItems,
          currentPage: currentPageState,
          publishRefresh,
        }),
      ),
    [currentPageState, pinnedItems, publishRefresh],
  );
  const {
    ready: subscriptionsReady,
    items: projects,
    totalItems,
    pageSize,
    renderPagination,
  } = usePagination({
    debounceMs: 1000,
    selector: ProjectSelect,
    getSubscription: (currentPage, resultsPerPage) => {
      // NOTE: It is used to refresh pinned items / re-run subscription by updating "version" on page change
      if (currentPage !== currentPageState) {
        setCurrentPageState(currentPage);
      }

      return apiAdminSearchProjects.withParams({
        filters: map(filters, (filter) => omit(filter, 'meta')),
        sorter,
        controlId: '$meta.id',
        pageIndex: currentPage - 1,
        resultsPerPage,
        version,
      });
    },
  });
  const dispatch = useDispatch();
  const handleAddProject = useCallback(
    () => dispatch(dialogCreateProject.open({})),
    [dispatch],
  );

  const handleOnDelete = useCallback(
    (projectId) =>
      dispatch(
        dialogArchiveProject.open({
          projectId,
        }),
      ),
    [dispatch],
  );
  const highlight = useHighlight(filters, ['name']);
  return (
    <Card>
      <DialogArchiveProject />
      <Stack>
        <div className="cluster-4">
          <Search className="flex-1" />
          {canCreateProjects && (
            <>
              {enableProjectWizardV2 && (
                <Button
                  data-testid="button-add-project"
                  type="primary"
                  onClick={handleAddProject}
                >
                  {t('createProject')}
                </Button>
              )}
            </>
          )}
        </div>
        <List
          data-testid="list-projects"
          title={t('totalWithCount', { count: totalItems })}
          dataSource={projects}
          loading={!subscriptionsReady}
          pageSize={pageSize}
          renderItem={(project) => (
            <ProjectItem
              key={project._id}
              projectId={project._id}
              name={project.name}
              description={project.description}
              draftSavedAt={project.draftSavedAt}
              error={getErrorText(project)}
              onDelete={handleOnDelete}
              highlight={highlight}
            />
          )}
          renderPagination={renderPagination}
        />
      </Stack>
    </Card>
  );
});

export default SettingsProjects;
